import { useEffect, useState } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { theme } from "@/lib/theme";

interface MagicLinkLoginProps {
  initialError?: string | null;
  messagingSetup?: string;
  embedded?: boolean;
  title?: string;
  description?: string;
}

type FeedbackState = {
  type: "success" | "error";
  message: string;
} | null;

export default function MagicLinkLogin({
  initialError = null,
  messagingSetup,
  embedded = false,
  title = "Welcome back",
  description = "Enter your email and we'll send you a magic link to sign in.",
}: MagicLinkLoginProps) {
  const { requestMagicLink, isRequestingLink, magicLinkSentTo, clearAuthError, authError } = useAuth();
  const [email, setEmail] = useState(magicLinkSentTo ?? "");
  const [feedback, setFeedback] = useState<FeedbackState>(
    initialError ? { type: "error", message: initialError } : null,
  );

  useEffect(() => {
    if (!magicLinkSentTo) return;
    setEmail(magicLinkSentTo);
    setFeedback({
      type: "success",
      message: `We just sent a sign-in link to ${magicLinkSentTo}. Check your inbox to continue.`,
    });
  }, [magicLinkSentTo]);

  useEffect(() => {
    if (!authError) return;
    setFeedback({ type: "error", message: authError });
  }, [authError]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    clearAuthError();

    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      setFeedback({ type: "error", message: "Please enter your email address." });
      return;
    }

    try {
      await requestMagicLink(normalizedEmail, messagingSetup ? { messagingSetup } : undefined);
      setFeedback({
        type: "success",
        message: `We just sent a sign-in link to ${normalizedEmail}. Check your inbox to continue.`,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "We could not send the sign-in link. Please try again.";
      setFeedback({ type: "error", message });
    }
  };

  const clearErrorFeedback = () => {
    if (feedback?.type !== "error") return;
    clearAuthError();
    setFeedback(null);
  };

  return (
    <div className={embedded ? "w-full" : `min-h-screen ${theme.colors.backgroundCream} flex items-center justify-center px-4 py-16`}>
      <div className={embedded ? "w-full" : "w-full max-w-md"}>
        <div className={embedded ? "" : `bg-white/80 dark:bg-gray-800/90 backdrop-blur ${theme.borders.neubrutalism} rounded-2xl ${theme.shadows.neubrutalismLg} px-8 py-10`}>
          <div className="text-center mb-8">
            <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full ${theme.colors.warmYellow} text-2xl`}>
              🎧
            </div>
            <h1 className="mt-6 text-3xl font-semibold text-gray-900 dark:text-white">{title}</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              {description}
            </p>
          </div>

          {feedback && (
            <div
              role={feedback.type === "error" ? "alert" : "status"}
              className={`mb-6 rounded-xl border px-4 py-3 text-sm ${
                feedback.type === "success"
                  ? `${theme.colors.warmYellowBorder} ${theme.colors.warmYellowBg} text-gray-900`
                  : "border-red-300 bg-red-50 text-red-700 dark:border-red-500/60 dark:bg-red-500/10 dark:text-red-200"
              }`}
            >
              {feedback.message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={event => {
                  setEmail(event.target.value);
                  if (feedback?.type === "error") {
                    clearAuthError();
                    setFeedback(null);
                  }
                }}
                onFocus={clearErrorFeedback}
                className={`mt-2 w-full rounded-xl ${theme.borders.neubrutalism} bg-white dark:bg-gray-900 px-4 py-3 text-base text-gray-900 dark:text-gray-100 ${theme.shadows.neubrutalismSm} focus:outline-none focus:ring-4 focus:ring-primary-orange/50 dark:focus:ring-primary-orange-dark/40`}
              />
            </div>

            <button
              type="submit"
              disabled={isRequestingLink}
              className={`w-full inline-flex items-center justify-center gap-2 rounded-xl ${theme.buttons.primary} px-4 py-3 text-base font-semibold transition-transform duration-150 focus:outline-none focus:ring-4 focus:ring-primary-orange/40 dark:focus:ring-primary-orange-dark/30 disabled:cursor-not-allowed disabled:opacity-75`}
            >
              {isRequestingLink ? "Sending link…" : "Send magic link"}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
            <p>Already have a link? Open it on this device and we&apos;ll sign you in automatically.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
